import { CreditCostMarker } from "@/components/features/Credits/CreditCostMarker";
import { Button } from "@/design-system/Button/Button";
import { SortDropdown } from "@/design-system/Dropdown/SortDropdown";
import { CoinIcon, MetaRatingIcon } from "@/designSystemIcons";
import { trackAddedPlayerToCompare } from "@/events/compareMixpanel";
import { SquadPreview } from "@/features/MyClub/ActiveSquad/ActiveSquadSection";
import { SquadWithPanel } from "@/features/MyClub/ActiveSquad/SquadWithPanel";
import {
  PitchPlayer,
  useActiveSquad,
  useSquadSwap,
} from "@/features/MyClub/ActiveSquad/useActiveSquad";
import { useEmptyClubGuard } from "@/features/MyClub/EmptyClub/useEmptyClubGuard";
import { cn } from "@/lib/utils";
import { openCompareSheet } from "@/redux/globalState/globalStateSlice";
import { addAlert } from "@/redux/message/messageSlice";
import { generateNavigationUrl } from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import { addUsedFeature, getUserSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import type { Recommendation } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import {
  REC_SORTS,
  type RecSort,
  recPlayerOnPitch,
  recsAreStale,
  sortRecommendations,
  useRecommendationSwaps,
} from "@repo/shared/myClub";
import {
  computeNewPlayerRecs,
  fetchRecommendations,
  myClubSelector,
  recommendationsSelector,
} from "@repo/shared/redux";
import {
  addComparePlayer,
  setCompareContext,
  useClubImportFeature,
  useCreditGate,
} from "@repo/shared/redux";
import { DetailedPlayer } from "@repo/shared/types";
import { formatTime } from "@repo/shared/utils";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import {
  PlayerChemistryIcon,
  SquadMetaRatingIcon,
  TeamChemistryIcon,
} from "./RecommendationIcons";
import { RecommendationUpgradeCard } from "./RecommendationUpgradeCard";

const SQUAD_ID = "recommendations-squad";
const COMPARE_ORIGIN = {
  section: "My Club",
  subsection: "Player Recommendations",
} as const;

type RecPlayer = NonNullable<Recommendation["newPlayer"]>;

/** A recommendation's player as the compare slots want it. */
const toComparePlayer = (player: RecPlayer) =>
  structuredClone({
    ...player,
    name: player.cardName,
    preferredPosition: player.position,
    evolutionData: player.evolutionPath
      ? { evolutionPath: player.evolutionPath, pathwayPath: player.pathwayPath }
      : undefined,
  }) as unknown as DetailedPlayer;

/* The same icons the recommendation figures have always used. */
const SORT_ICONS: Record<RecSort, ReactNode> = {
  squadMeta: <MetaRatingIcon width="16" height="16" />,
  playerMeta: <SquadMetaRatingIcon width="16" height="16" />,
  squadChem: <TeamChemistryIcon />,
  playerChem: <PlayerChemistryIcon />,
  price: <CoinIcon size={16} />,
};

/** A recommended card as web's pitch draws it. */
const toPitchPlayer = (player: RecPlayer) =>
  recPlayerOnPitch(player) as unknown as PitchPlayer;

const CardSkeleton = () => (
  <div className="h-48 animate-pulse rounded-xl border border-neutral-700 bg-neutral-800" />
);

/**
 * Player recommendations for the active squad: the same pitch as the club
 * home, with the upgrades listed beside it (below it on phones), so each one
 * reads as a change to that squad. Preview swaps an upgrade into the pitch.
 */
export const PlayerRecommendations = () => {
  const { t } = useTranslation(["myClub", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const recommendations = useSelector(recommendationsSelector);
  const { fetchRecommendationStatus, computeRecommendationStatus, clubStats } =
    useSelector(myClubSelector);
  const hasClub = useClubImportFeature("see-active-squad").allowed;
  const creditGate = useCreditGate("squad-recommendations-club");
  // Free until the onboarding's recommendations step is ticked complete.
  const { user } = useSelector(getUserSelector);
  const updateIsFree = !user?.usedFeatures?.includes(
    "MY_CLUB_ONBOARDING_RECOMMENDATIONS",
  );
  const { guardClubAction } = useEmptyClubGuard({
    section: "My Club",
    subsection: "Overview",
  });
  // Reads the squad the section above fetches.
  const { squad: active, source } = useActiveSquad({ enabled: false });
  const swap = useSquadSwap(source, active);
  const [previewId, setPreviewId] = useState<string>();
  const [sort, setSort] = useState<RecSort>("squadMeta");

  const { swaps, figuresOf } = useRecommendationSwaps(
    recommendations,
    active,
    swap,
    toPitchPlayer,
  );
  const sorted = useMemo(
    () => sortRecommendations(recommendations, sort, figuresOf),
    [recommendations, sort, figuresOf],
  );
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);

  useEffect(() => {
    if (hasClub) dispatch(fetchRecommendations());
  }, [hasClub, dispatch]);

  const isLoading =
    fetchRecommendationStatus === "pending" ||
    computeRecommendationStatus === "pending";

  const update = async () => {
    // Every paid call is Qualifying: recommendations come from the squad.
    if (!creditGate({ qualifying: !updateIsFree, prompt: true })) return;
    setPreviewId(undefined);
    const result = await dispatch(computeNewPlayerRecs());
    // The old list stays up on a failure, so say the update didn't happen.
    if (computeNewPlayerRecs.rejected.match(result))
      dispatch(
        addAlert({ type: "error", description: t("common:error.generic") }),
      );
    // The free update ticks the onboarding step on the account (the backend
    // does it, on any non-empty result); show that now rather than on the
    // next profile load, so the next update shows its Credit.
    else if (updateIsFree && result.payload?.length)
      dispatch(addUsedFeature("MY_CLUB_ONBOARDING_RECOMMENDATIONS"));
  };

  /*
    Recommendations are computed for one version of the squad. If any of them
    swaps out a player who is no longer in it, the squad has changed since,
    and none of them can be trusted: they are greyed out until updated.
  */
  const stale = recsAreStale(recommendations, source);

  const preview = useMemo<SquadPreview | undefined>(() => {
    const previewSwap = previewId ? swaps.get(previewId) : undefined;
    if (stale || !previewSwap) return undefined;
    return { ...previewSwap, onClear: () => setPreviewId(undefined) };
  }, [previewId, swaps, stale]);

  const togglePreview = (rec: Recommendation) => {
    const next = previewId === rec.id ? undefined : rec.id;
    setPreviewId(next);
    // On phones the pitch is above the list, so bring it into view.
    if (next && isMobile)
      document
        .getElementById(SQUAD_ID)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const compare = (rec: Recommendation) => {
    if (!rec.oldPlayer?.resourceId || !rec.newPlayer) return;
    // Added beside whatever the user already staged, never instead of it.
    dispatch(setCompareContext({ playerRoleId: rec.oldPlayer.playerRoleId }));
    dispatch(addComparePlayer(toComparePlayer(rec.oldPlayer)));
    dispatch(addComparePlayer(toComparePlayer(rec.newPlayer)));
    trackAddedPlayerToCompare({
      origin: COMPARE_ORIGIN,
      source: "card",
      player_count: 2,
    });
    dispatch(openCompareSheet({ exitOnClose: true }));
  };

  const openPlayer = (player: RecPlayer) =>
    navigate(
      generateNavigationUrl({
        playerRoleId: player.playerRoleId,
        resourceId: player.resourceId,
        name: player.cardName,
        evolutionPath: player.evolutionPath,
        pathwayPath: player.pathwayPath,
        evoPathId: player.evoPathId,
        canEvolve: player.canEvolve,
        basePath: "/players",
      }),
    );

  const updatedAt = clubStats.squadRecsLastUpdatedAt;

  const cards = isLoading ? (
    Array.from({ length: 4 }, (_, i) => <CardSkeleton key={i} />)
  ) : sorted.length ? (
    sorted.map((rec) => {
      return (
        <RecommendationUpgradeCard
          key={rec.id}
          recommendation={rec}
          figures={figuresOf(rec)}
          previewing={previewId === rec.id}
          stale={stale}
          onPreview={() => togglePreview(rec)}
          onCompare={() => compare(rec)}
          onOpenPlayer={() => rec.newPlayer && openPlayer(rec.newPlayer)}
        />
      );
    })
  ) : (
    <p className="rounded-xl border border-neutral-700 bg-neutral-800 px-5 py-8 text-center text-sm text-foreground-muted">
      {fetchRecommendationStatus === "rejected"
        ? t("recommendations.card.errorBody")
        : t("recommendations.notFound")}
    </p>
  );

  return (
    <SquadWithPanel
      fitCards
      panelFirstOnPhone
      squad={{
        enabled: hasClub,
        id: SQUAD_ID,
        preview,
        showBestSquad: false,
      }}
      header={
        <>
          {/* First, so the reason to update sits right above the button. */}
          {stale && !isLoading && (
            <p className="rounded-xl border border-orange-900 bg-orange-950/40 px-3.5 py-2.5 text-sm leading-snug text-orange-300">
              {t("recs.stale")}
            </p>
          )}
          {isTouch ? (
            // Full width on touch, as in the app: the cost in front of it.
            <Button
              variant="tertiary"
              size="medium"
              stretch
              disabled={isLoading}
              onClick={() => guardClubAction(update)}
            >
              <span className="flex items-center gap-2">
                <CreditCostMarker
                  featureId="squad-recommendations-club"
                  when={!updateIsFree}
                />
                <span className="text-holo">{t("recommendations.update")}</span>
              </span>
            </Button>
          ) : (
            <div className="flex items-center justify-end gap-2">
              <CreditCostMarker
                featureId="squad-recommendations-club"
                when={!updateIsFree}
              />
              {/* The solver's holo label, like Build Solutions on SBC cards. */}
              <Button
                variant="tertiary"
                size="medium"
                disabled={isLoading}
                onClick={() => guardClubAction(update)}
              >
                <span className="text-holo">{t("recommendations.update")}</span>
              </Button>
            </div>
          )}

          {(updatedAt > 0 || recommendations.length > 0) && (
            <div className="-mt-1 flex items-center gap-3">
              {updatedAt > 0 && (
                <span className="inline-block min-w-0 truncate text-sm text-foreground-muted first-letter:uppercase">
                  {t("recs.updated", {
                    time: formatTime(Date.now() - updatedAt),
                  })}
                </span>
              )}
              {recommendations.length > 0 && (
                <div className="ml-auto">
                  <SortDropdown
                    items={REC_SORTS.map((value) => ({
                      value,
                      label: t(`recs.sort.${value}`),
                      icon: (
                        <span className="flex size-4 items-center justify-center text-neutral-300 [&>svg]:max-h-4 [&>svg]:max-w-4">
                          {SORT_ICONS[value]}
                        </span>
                      ),
                    }))}
                    selected={sort}
                    label={t("recs.sortBy", { sort: t(`recs.sort.${sort}`) })}
                    onChange={(value) => setSort(value as RecSort)}
                  />
                </div>
              )}
            </div>
          )}
        </>
      }
    >
      {cards}
    </SquadWithPanel>
  );
};
